<template>
  <div
    class="paragraph-teaser-slider 2xl:!w-[70%] 2xl:!max-w-[1920px] container relative w-[90%] ml-0 sm:ml-auto transition sm:w-full mx-auto"
    data-allow-mismatch
    :class="{
      'opacity-0': !isLoaded,
    }"
  >
    <Swiper v-bind="options" @swiper="getRef">
      <SwiperSlide v-for="(image, i) in slidesToRender" :key="'first_' + i">
        <slot name="item" v-bind="image" />
      </SwiperSlide>
    </Swiper>
    <div class="mt-6 grid-cols-12 gap-3 grid">
      <div class="col-span-7">
        <div class="flex gap-4">
          <button
            :class="[
              'flex items-center justify-center size-11 rounded-full border border-lightGreen bg-transparent',
              { 'opacity-50': isBeginning },
            ]"
            :disabled="isBeginning"
            @click="goPrev"
          >
            <SpriteSymbol name="chevron-left" class="size-6 text-darkGreen" />
            <span class="sr-only">{{
              $texts('slides.prev')
            }}</span>
          </button>
          <button
            :class="[
              'flex items-center justify-center size-11 rounded-full border border-lightGreen bg-transparent',
              { 'opacity-50': isEnd },
            ]"
            :disabled="isEnd"
            @click="goNext"
          >
            <SpriteSymbol name="chevron-right" class="size-6 text-darkGreen" />
            <span class="sr-only">{{ $texts('slides.next') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Swiper as SwiperClass } from 'swiper/types'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
import type { MediaImageFragment } from '#graphql-operations'

const props = defineProps<{
  slides: (MediaImageFragment | null)[] | null
}>()

const isLoaded = ref(false)
const { $texts } = useEasyTexts()

const slidesToRender = computed(() => {
  // If no slides are provided, return empty array
  if (!props.slides?.length) {
    return []
  }

  return props.slides
})

const swiper = ref<SwiperClass | null>(null)

const isBeginning = computed(() => {
  return swiper.value?.isBeginning
})

const isEnd = computed(() => {
  return swiper.value?.isEnd
})

function getRef(swiperInstance: SwiperClass) {
  swiper.value = swiperInstance
}

function goNext() {
  if (swiper.value) {
    swiper.value.slideNext()
  }
}
function goPrev() {
  if (swiper.value) {
    swiper.value.slidePrev()
  }
}

type SwiperProps = InstanceType<typeof Swiper>['$props']

const options = computed<SwiperProps>(() => {
  return {
    speed: 600,
    initialSlide: 0,
    spaceBetween: 24,
    loopedSlides: 0,
    loop: false,
    breakpoints: {
      xs: {
        width: 100,
      },
      650: {
        width: 550,
      },
      768: {
        width: 668,
      },
      1000: {
        width: 912,
      },
    },
  }
})

onMounted(() => {
  if (swiper.value) {
    isLoaded.value = true
  }
})
</script>

<style lang="postcss">
.swiper {
  @apply overflow-visible;
}
.swiper-button-prev {
  @apply size-7 bg-primary-500;
}
</style>
